import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { animated, useSpring } from '@react-spring/web';

import { custom } from '../custom';
import { svg } from '../assets/svg';
import { theme } from '../constants';
import { actions } from '../store/actions';
import { items as ShopItem } from '../items';
import { hooks, RootState } from '../hooks';
import { components } from '../components';

const sortingBy = [
  { id: 1, title: 'Sale' },
  { id: 2, title: 'Top' },
  { id: 3, title: 'Newest' },
  { id: 4, title: 'Price: low to high' },
  { id: 5, title: 'Price: high to low' },
];

export const Shop: React.FC = () => {
  const dispatch = hooks.useAppDispatch();
  const navigate = hooks.useAppNavigate();
  const location = useLocation();
  const state = location.state || {};
  const title = state.title || 'Shop';
  const products = state.products || [];
  const [sort, setSort] = useState(sortingBy[0]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState(products.slice(0, 20));
  const [hasMore, setHasMore] = useState(products.length > 20);
  // We can keep isMobile state for other purposes if needed, 
  // but it's no longer used for the grid layout in renderContent
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); 

  const backdropAnimation = useSpring({
    opacity: modalIsOpen ? 1 : 0,
    display: modalIsOpen ? 'flex' : 'none',
  });
  

  useEffect(() => {
    document.body.style.overflow = modalIsOpen ? 'hidden' : 'auto';
  }, [modalIsOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    selectedColors,
    selectedSizes,
    selectedCategories,
    selectedTags,
  } = hooks.useAppSelector((state: RootState) => state.filterSlice);

  const cart = hooks.useAppSelector((state: RootState) => state.cartSlice.list);
  const total = hooks.useAppSelector((state: RootState) => state.cartSlice.total);

  const filteredProducts = products.filter((product: any) => {
    const sizeMatch =
      selectedSizes.length === 0
        ? true
        : selectedSizes.some((size) => product.sizes.includes(size));
    const colorMatch =
      selectedColors.length === 0
        ? true
        : selectedColors.some((selectedColor) =>
            product.colors.some(
              (productColor: any) => productColor.name === selectedColor
            )
          );
    const tagMatch =
      selectedTags.length === 0
        ? true
        : selectedTags.some((tag) => product.tags.includes(tag));
    const statusMatch =
      selectedCategories.length === 0
        ? true
        : (product.isNew && selectedCategories.includes('new')) ||
          (product.isTop && selectedCategories.includes('top')) ||
          (product.oldPrice && selectedCategories.includes('sale'));
    return colorMatch && statusMatch && tagMatch && sizeMatch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sort.title) {
      case 'Price: low to high':
        return a.price - b.price;
      case 'Price: high to low':
        return b.price - a.price;
      case 'Newest':
        return a.isNew === b.isNew ? 0 : a.isNew ? -1 : 1;
      case 'Top':
        return a.isTop === b.isTop ? 0 : a.isTop ? -1 : 1;
      case 'Sale':
        return a.oldPrice === b.oldPrice ? 0 : a.oldPrice ? -1 : 1;
      case 'Featured':
        return a.isFeatured === b.isFeatured ? 0 : a.isFeatured ? -1 : 1;
      default:
        return 0;
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchMoreData = () => {
    if (items.length >= products.length) {
      setHasMore(false);
      return;
    }
    setTimeout(() => {
      setItems(items.concat(products.slice(items.length, items.length + 20)));
    }, 500);
  };

  const handleOnClick = () => {
    if (cart.length > 0) {
      dispatch(actions.setScreen('Order'));
      navigate('/TabNavigator');
    } else {
      alert('Your cart is empty');
    }
  };

  const renderHeader = (): JSX.Element => {
    return <components.Header goBack={true} burger={true} basket={true} line={true} />;
  };

const renderContent = () => (
    <div
        style={{ width: '100%', flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '15px 15px 10px 15px', marginBottom: '40px' }}
    >
        <div // GRID CONTAINER: Fixed 2 columns
            style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, boxSizing: 'border-box' }}
        >
            {sortedProducts.map((reviewItem, index, array) => (
                <div // GRID ITEM WRAPPER: NOW WITH CLASS
                    key={reviewItem.id}
                    className="product-card-wrapper" // <--- CRITICAL: ADD THIS CLASS
                    style={{ boxSizing: 'border-box' }} // width: '100%' is likely unnecessary here
                > 
                    <ShopItem.ProductCard
                        isLast={index === array.length - 1}
                        item={reviewItem}
                        version={2}
                        // style prop removed to avoid TypeScript error
                    />
                </div>
            ))}
        </div>
    </div>
);

  const renderModal = (): JSX.Element => (
    <animated.div
      className="container"
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(25, 51, 100, 0.6)',
        position: 'fixed',
        inset: 0,
        zIndex: 22,
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        ...backdropAnimation,
      }}
      onClick={() => setIsOpen(false)}
    >
      <div
        style={{
          width: 'calc(100% - 80px)',
          backgroundColor: 'white',
          margin: '0 20px',
          borderRadius: 5,
          padding: '10px 0 0 20px',
        }}
      >
        {sortingBy.map((item) => (
          <button
            key={item.id}
            style={{
              width: '100%',
              padding: '10px 20px 15px 0',
              borderBottom: '1px solid #E5E5E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => {
              setSort(item);
              setIsOpen(false);
            }}
          >
            <span
              style={{
                ...theme.fonts.Mulish_400Regular,
                fontSize: 10,
                color: theme.colors.textColor,
                lineHeight: 1.7,
              }}
            >
              {item.title}
            </span>
            <div
              style={{
                width: 17,
                height: 17,
                border: '3px solid #E8EFF4',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {sort.id === item.id && (
                <div
                  style={{
                    width: 9,
                    height: 9,
                    backgroundColor: theme.colors.mainColor,
                    borderRadius: '50%',
                  }}
                />
              )}
            </div>
          </button>
        ))}
      </div>
    </animated.div>
  );

  const renderBottomTabBar = () => {
    return <components.BottomTabBar />;
  };

  return (
    // Main container now uses Flexbox
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {renderHeader()}
      {renderContent()}
      {renderModal()}
      {renderBottomTabBar()}
    </div>
  );
};